/**
 * Define the currently configured feature flags in LaunchDarkly here
 *
 * @note this file may be empty if there are currently no feature flags configured
 * @link https://app.launchdarkly.com/projects/default/flags
 */
export const APPhysicalCheckPrintingEnabled = "ap-physical-check-printing-enabled";
export const ARSnapshotReportEnabled = "ar-snapshot-report-enabled"; // Controls both AR and AP snapshot reports
export const featureFlagAiEnabled = "ai-enabled";
/** Default-off Assistant Fast mode. Enrollment is resolved before conversation acquisition. */
export const assistantFastTierEnabled = "ai-assistant-fast-tier-enabled";
export const documentAgentEnabled = "document-agent-enabled";
export const isDocumentAgentDefaultEnabled = () =>
  process.env.NEXT_PUBLIC_ENV === "localhost" || process.env.NEXT_PUBLIC_ENV === "development";
export const featureFlagStatementMatchEnabled = "ai-statement-match-enabled";
export const isStatementMatchDefaultEnabled = () =>
  process.env.NEXT_PUBLIC_ENV === "localhost" || process.env.NEXT_PUBLIC_ENV === "development";
export const orderLineItemPercentagesEnabled = "order-line-item-percentages-enabled";
/** Certificate of Analysis generator (Inventory → Lots COAs). Off unless the flag targets the org. */
export const coaGeneratorEnabled = "coa-generator-enabled";
export const isCoaGeneratorDefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
export const COA_GENERATOR_DISABLED_MESSAGE = "Certificates of Analysis are not enabled for this organization.";

/**
 * Flags that are always on when running on localhost, whatever LaunchDarkly's `local` environment serves (an explicit
 * "off" there would otherwise override the localhost default). Read by both the server and the browser flag lookups.
 */
const LOCALHOST_FORCED_ON_FLAGS: readonly string[] = [coaGeneratorEnabled];
export const isFlagForcedOnLocally = (flagKey: string): boolean =>
  process.env.NEXT_PUBLIC_ENV === "localhost" && LOCALHOST_FORCED_ON_FLAGS.includes(flagKey);
export const reportsV2Enabled = "reports-v2-enabled";
export const salesOrderAutomationEnabled = "sales-order-automation-enabled";
export const salesOrderItemLabelsEnabled = "sales-order-item-labels-enabled";
export const apCostsIndexEnabled = "ap-costs-index";
export const monthlyFinancialWorkbookEnabled = "monthly-financial-workbook-enabled";
export const multiCurrencyEnabled = "multi-currency-enabled";
export const isMultiCurrencyDefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
/**
 * Invoice Decoupling multi-order experience. The association foundation and access to
 * existing invoice history remain active regardless of this flag. New plural membership,
 * consolidated posting (including already-open drafts), consolidated reinvoice-number reuse,
 * and multi-order controls/presentation are gated. Corrective void/reversal paths stay
 * available so disabling rollout cannot strand financial records. Flag-off interactive
 * surfaces use the legacy first-order projection; issued grouped PDFs retain their
 * association-backed form so a flag flip cannot change a customer document.
 */
export const multiOrderInvoiceEnabled = "multi-order-invoice-enabled";
export const isMultiOrderInvoiceDefaultEnabled = () => false;
export const MULTI_ORDER_INVOICE_DISABLED_MESSAGE = "Multi-order invoicing is disabled for this organization.";
export const MULTI_ORDER_REINVOICE_DISABLED_MESSAGE = [
  "This voided invoice covered multiple Sales Orders.",
  "Enable multi-order invoicing to reuse its invoice number,",
  'or turn off "Re-Use voided invoice number?" to create a new single-order invoice.',
].join(" ");
/** Project 2 capability/UI rollout gate. Foundational invoice allocation writes are always active. */
export const advancedBillingShipmentDecouplingEnabled = "advanced-billing-shipment-decoupling-enabled";
export const isAdvancedBillingShipmentDecouplingDefaultEnabled = () => false;
export const announcementBannerText = "announcement-banner-string";
export const salesPerformancePage = "sales-performance-page";
/**
 * Work Order V2 — per-org gate for the new WO subsystem.
 *
 * Per `src/modules/work-order-v2/docs/features/WOV2/v1-to-v2-org-migration.md` (§ The three flags), the
 * contract is:
 *   * OFF (default): V1 only; V2 create endpoints reject.
 *   * ON:           V2 available for new WOs; V1 create stays until
 *                   `work-order-v1-enabled` is off; with V1 off, V1 is a read-only
 *                   archive (list/detail accessible, edits refused).
 *
 * The flag is per-org. Server-side enforcement lives in
 * `~/server/api/routers/v2/work-orders-router.ts`'s create procedure
 * (Step 16). UI uses `useWorkOrderV2Enabled()` to gate visibility.
 *
 * On localhost the default flips to `true` so engineers can browser-
 * verify the V2 surface without round-tripping through LaunchDarkly.
 * Production / staging stay default-OFF — the rollout doc's safety
 * contract is unchanged.
 *
 * Unlike `isStatementMatchDefaultEnabled` / `isDocumentAgentDefaultEnabled`,
 * the `development` environment is intentionally NOT default-ON here:
 * this PR reroutes V1 PO/bill/lot financial paths through the V2
 * amendment coordinator, and `development` is a shared multi-user
 * preview environment. A default-ON flag there would silently change
 * V1 accounting behavior for everyone on that environment. V2 stays
 * opt-in via LaunchDarkly outside localhost.
 */
export const workOrderV2Enabled = "work-order-v2-enabled";
export const isWorkOrderV2DefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
/**
 * Work Order V1 — per-org gate for new V1 work orders (create, copy, create-from-copy) and for
 * edits to existing V1 records (header, status, items, costs, lots, reopen).
 * Default ON when LaunchDarkly has no value, preserving legacy V1 availability.
 * When off, V1 records are a read-only archive: reads, CSV export, and document creation stay
 * available. Contract: `src/modules/work-order-v2/docs/features/WOV2/v1-to-v2-org-migration.md`
 * (Phase 3). Writers that still touch an archived record include bill amend / void of EXISTING costs
 * and Migrate to V2 (`migrateFromWorkOrderV1`); full list: the doc's Phase 3.
 * Bill Quick Add of a NEW cost onto a V1 work order is refused (`processBillPendingActions`) and the
 * Quick Add picker omits V1 work orders (`bills.getOrderCandidates`).
 */
export const workOrderV1Enabled = "work-order-v1-enabled";
export const isWorkOrderV1DefaultEnabled = () => true;
/**
 * Variable Packages on Lot — per-org gate for lot container attach/remove on VARIABLE lots.
 * Gates exactly the surfaces WOV2 gated before this flag existed; it does NOT disable variable
 * packaging (products keep VARIABLE and POs keep receiving VARIABLE lots). V1 refuses VARIABLE
 * output completion only while V1, V2, and this flag are all enabled.
 * Default OFF when LaunchDarkly has no value and ON on localhost for local simulation.
 */
export const variablePackagesOnLotEnabled = "variable-packages-on-lot-enabled";
export const isVariablePackagesOnLotDefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
/**
 * Voice Order Entry — per-org gate for dictation on the New Order form.
 * Prod default-OFF until Deepgram no-retention/DPA is confirmed; localhost default-ON for dev.
 */
export const voiceOrderEntryEnabled = "voice-order-entry-enabled";
export const isVoiceOrderEntryDefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
/**
 * Plaid bank feed + Live Receivables — per-org gate for Plaid link UI, inbound deposit
 * sync/matching, superadmin Plaid setup, and related tRPC surfaces. OFF (default): hidden
 * UI and FORBIDDEN APIs. ON: full bank-feed flow for the org.
 */
export const plaidBankFeedEnabled = "plaid-bank-feed-enabled";
export const isPlaidBankFeedDefaultEnabled = () => process.env.NEXT_PUBLIC_ENV === "localhost";
/**
 * Output templates (the customer-facing "Templates" builder) — per-org beta gate, on top of the
 * `outputTemplates.access` permission. The two are independent: the permission says who in an org
 * may use the feature, this says whether the org is in the beta at all. OFF (default): the nav
 * entry is hidden. ON: the entry appears for users who also hold the permission.
 *
 * Default-on for localhost and development so the builder can be exercised without a dashboard
 * rule; every other environment is off until the flag targets the org.
 */
export const outputTemplatesEnabled = "output-templates-enabled";
export const isOutputTemplatesDefaultEnabled = () =>
  process.env.NEXT_PUBLIC_ENV === "localhost" || process.env.NEXT_PUBLIC_ENV === "development";
